import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Target, Users, Layers, Zap, ShieldCheck, Globe2 } from "lucide-react";
import { LegalHeader, LegalFooter } from "./legal/Terms";
import useSEO from "../lib/useSEO";

const PILLARS = [
  { icon: Layers,     h: "One home for delivery",       p: "SAP and D365 programmes still run out of spreadsheets, decks and inboxes. Capittance replaces that stack with a single delivery operating system — every artefact, every KPI, every risk in one live workspace." },
  { icon: Target,     h: "Boardroom-ready by default",  p: "Every screen exports a magazine-grade PDF, PPT or Word deck. The Executive Portal gives CIOs and CFOs a real-time snapshot without waiting for the weekly steering committee." },
  { icon: Sparkles,   h: "AI where it earns its keep",  p: "NAVBOT (Claude Sonnet 4.5) is your executive AI assistant. It summarises RAID logs, drafts board updates, and answers portfolio questions in plain English — grounded in your authorized project data only. Activates the moment your C-Suite subscription goes live." },
  { icon: ShieldCheck,h: "Enterprise trust from day one",p: "Bcrypt password hashing, TLS 1.2+, subscription-gated access, full audit trail, GDPR / UK-GDPR / CCPA support, and connector secrets that never leave the server." },
  { icon: Users,      h: "Built for the whole delivery cadre", p: "Operators run their projects in the Basic Portal ($200/mo). CIOs, CFOs, and portfolio owners live in the Executive Portal ($350/mo) with live dashboards and one-click Board Packs." },
  { icon: Globe2,     h: "Live tenant integrations",    p: "Connect your Microsoft Dynamics 365, SAP S/4HANA or Oracle Cloud ERP tenant with read-only credentials. Cockpits swap from demonstration data to your tenant data once we validate the connection — typically same-day, no data warehouse required." },
];

const TIMELINE = [
  { year: "2024", h: "The problem",   p: "After leading multi-year SAP programmes, our founder saw the same pain repeat: teams reinvent RAID logs, cutover runbooks, and value trackers on every engagement. Board packs are hand-assembled every month." },
  { year: "2025", h: "The prototype", p: "Harmonizer began as an internal tool — the same delivery pattern, digitised. Autosave. Field-scoped updates. Exports. A tracker that actually reflected reality." },
  { year: "2026", h: "The platform",  p: "Capittance launched publicly with two tiers, an AI copilot, live D365 / S/4HANA connectors, and a 20-slide one-click Board Pack. This is the delivery OS we wished we'd had ten years ago." },
];

export default function AboutPage() {
  useSEO({
    title: "About Capittance · Manifesto & founding story of the SAP delivery OS",
    description: "SAP and Dynamics 365 programmes spend more on reporting than delivery. Read the Capittance Harmonizer manifesto — one live workspace with an AI copilot replacing the deck-and-spreadsheet stack.",
    path: "/about",
  });
  return (
    <div className="min-h-screen bg-white">
      <LegalHeader title="About Capittance" subtitle="The delivery OS for SAP & Dynamics 365 programmes" />
      <main className="max-w-4xl mx-auto px-6 py-10" data-testid="page-about">
        {/* Manifesto */}
        <div className="mb-14">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Manifesto</div>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-zinc-900 leading-tight">
            Boardroom-grade delivery, without the boardroom-grade overhead.
          </h1>
          <p className="text-base text-zinc-700 mt-5 leading-relaxed max-w-3xl">
            SAP and Microsoft Dynamics 365 transformations spend more on reporting than on delivery. Slide decks are rebuilt every Friday. Risks and issues live in three tools none of which talk to each other. Value promised to the board rarely gets measured after go-live.
          </p>
          <p className="text-base text-zinc-700 mt-4 leading-relaxed max-w-3xl">
            We built <b>Capittance Harmonizer</b> to close that gap — one platform that runs the programme <em>and</em> reports on it, with a Claude 4.5 copilot that turns portfolio noise into three-bullet decisions.
          </p>
        </div>

        {/* Pillars */}
        <div className="mb-14">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">What we stand for</div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight mb-6">Six product principles</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="card p-4" data-testid={`about-pillar-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-sm bg-cyan-50 grid place-items-center text-cyan-700"><Icon size={16}/></div>
                    <div className="font-display text-base font-bold tracking-tight">{p.h}</div>
                  </div>
                  <div className="text-sm text-zinc-700 leading-relaxed">{p.p}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-14">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Timeline</div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight mb-6">How we got here</h2>
          <div className="space-y-4">
            {TIMELINE.map((t, i) => (
              <div key={i} className="flex gap-4 items-start" data-testid={`about-timeline-${i}`}>
                <div className="font-display text-3xl font-extrabold text-cyan-700 w-20 shrink-0">{t.year}</div>
                <div>
                  <div className="font-display text-lg font-bold tracking-tight">{t.h}</div>
                  <div className="text-sm text-zinc-700 leading-relaxed mt-1 max-w-2xl">{t.p}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="card p-8 text-center relative overflow-hidden mb-10" style={{ background: "linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)" }}>
          <div className="font-mono text-[10px] uppercase tracking-widest text-sky-800 mb-2">Try it</div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">See if Capittance fits your team</h2>
          <p className="text-sm text-zinc-700 mt-2 max-w-xl mx-auto">Start on the Basic Portal ($200/mo) or go straight to the Executive Portal ($350/mo). Cancel anytime.</p>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/pricing" className="btn btn-secondary btn-sm" data-testid="about-see-pricing">See pricing</Link>
            <Link to="/signup" className="btn btn-primary btn-sm" data-testid="about-signup">Choose a plan →</Link>
          </div>
        </div>

        <LegalFooter />
      </main>
    </div>
  );
}
